import { get, writable } from "svelte/store";
import API from "../../API/API";
import LoadState from "../../Enums/LoadState";
import GeoHelper from "../../Helpers/GeoHelper";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";
import MapLayerManager from "../../Map/MapLayerManager";
import PopupStore from "../../Map/Popup/PopupStore";
import MapStore from "../../Map/Store";

const RiskIntPortsStore = {
	meta: {
		visible: writable(false),
		loadState: writable(LoadState.INIT),
		error: writable(""),
		lastUpdated: writable(Date.now()),
		layerName: "riskint_ports",
		mapInitialised: writable(false),

		portVisible: writable(false),
		selectedPort: writable(null)
	},
	results: {
		ports: writable([]),
		geojson: writable(GeoHelper.emptyGeoJson()),

		portSubcategoryData: {
			"threat-assessment": {},
			"security-risk-assessment": {},
			"vulnerability-assessment": {},
			documents: {},
			facilities: {}
		}
	},
	helpers: {
		findPortById: (id) => {
			const ports = get(RiskIntPortsStore.results.ports);
			for (let i = 0; i < ports.length; i++) {
				if (ports[i].id === id) {
					return ports[i];
				}
			}
			return null;
		},
		_setupPortSubDataType: (portId, subcategory) => {
			// This is all a bit hacky.
			// TODO: create and use a data Model.
			const port = RiskIntPortsStore.helpers.findPortById(portId);
			if (!port.subcategoryData) {
				port.subcategoryData = {};
			}
			if (!port.subcategoryData[subcategory]) {
				port.subcategoryData[subcategory] = {};
				port.subcategoryData[subcategory].loadState = writable(LoadState.INIT);
				port.subcategoryData[subcategory].result = writable(null);

				RiskIntPortsStore.helpers._updatePort(port);
			}
			return port;
		},
		_updatePort: (port) => {
			const ports = get(RiskIntPortsStore.results.ports);
			for (let i = 0; i < ports.length; i++) {
				if (ports[i].id === port.id) {
					ports[i] = port;
				}
			}
			RiskIntPortsStore.results.ports.set(ports);
			RiskIntPortsStore.meta.lastUpdated.set(Date.now());
		},
		doesPortHaveSubcategoryData: (portId, subcategory) => {
			const port = RiskIntPortsStore.helpers._setupPortSubDataType(portId, subcategory);
			return get(port.subcategoryData[subcategory].loadState) === LoadState.LOADED;
		},
		getPortSubcategory: (portId, subcategory) => {
			const port = RiskIntPortsStore.helpers._setupPortSubDataType(portId, subcategory);
			return port.subcategoryData[subcategory];
		}
	},
	actions: {
		loadSubcategoryData: (portId, subcategory) => {
			if (RiskIntPortsStore.helpers.doesPortHaveSubcategoryData(portId, subcategory)) {
				return;
			}
			const port = RiskIntPortsStore.helpers._setupPortSubDataType(portId, subcategory);
			const data = port.subcategoryData[subcategory];
			data.loadState.set(LoadState.LOADING);
			API.get("riskint/port?id=" + portId + "&type=" + subcategory)
				.then((result) => {
					data.loadState.set(LoadState.LOADED);
					data.result.set(result);
				})
				.catch((error) => {
					console.error(error);
				});
		},
		init: (map) => {
			if (get(RiskIntPortsStore.meta.loadState) !== LoadState.INIT) {
				return;
			}

			RiskIntPortsStore.meta.error.set("");
			RiskIntPortsStore.meta.loadState.set(LoadState.LOADING);

			API.get("riskint/ports")
				.then((result) => {
					RiskIntPortsStore.results.ports.set(result.ports);

					const markerUrl = "/img/ri/port@2x.png";
					const imageId = "marker-RI-port";
					if (!map.hasImage(imageId)) {
						map.loadImage(markerUrl, (error, image) => {
							if (error) {
								console.warn("could not load ri port icon", error);
								throw error;
							}
							map.addImage("marker-RI-port", image);

							RiskIntPortsStore.meta.error.set("");
							RiskIntPortsStore.meta.lastUpdated.set(Date.now());
							RiskIntPortsStore.meta.loadState.set(LoadState.LOADED);

							RiskIntPortsStore.actions.updateFeatureCollection();
						});
					}
				})
				.catch((e) => {
					console.error(e);
					RiskIntPortsStore.meta.error.set(e.data.detail.message);
					RiskIntPortsStore.meta.loadState.set(LoadState.INIT);
				});
		},
		openPort: (portId) => {
			const port = RiskIntPortsStore.helpers.findPortById(portId);
			if (port !== null) {
				RiskIntPortsStore.meta.selectedPort.set(port);
				RiskIntPortsStore.meta.portVisible.set(true);
			}
		},
		updateFeatureCollection: () => {
			const ports = get(RiskIntPortsStore.results.ports);
			const features = [];
			for (let i = 0; i < ports.length; i++) {
				const port = ports[i];
				const feature = GeoHelper.featurePoint([port.position.longitude, port.position.latitude]);
				feature.properties = port;
				feature.properties.iconImage = "marker-RI-port";
				features.push(feature);
			}
			const geojson = GeoHelper.emptyGeoJson(features);
			RiskIntPortsStore.results.geojson.set(geojson);

			if (get(RiskIntPortsStore.meta.mapInitialised)) {
				const map = MapStore.helpers.getMap();
				const idName = RiskIntPortsStore.meta.layerName;
				map.getSource(idName).setData(geojson);
			}
		},
		initMap: (map) => {
			if (get(RiskIntPortsStore.meta.mapInitialised)) {
				return;
			}

			const idName = RiskIntPortsStore.meta.layerName;
			if (typeof map.getSource(idName) === "undefined") {
				map.addSource(idName, {
					type: "geojson",
					data: GeoHelper.emptyGeoJson()
				});

				map.addLayer({
					id: idName,
					source: idName,
					type: "symbol",
					layout: {
						"icon-image": ["get", "iconImage"],
						"icon-allow-overlap": true,
						"icon-size": 0.5,
						"icon-anchor": "bottom"
					}
				});
				MapLayerManager.addLayerGroup(MapLayerGroup.RISKINT_PORTS, [idName], [idName]);

				const onLayerClick = (e) => {
					e.preventDefault();

					if (e.features.length > 0) {
						const feature = e.features[0];
						const portId = feature.properties.id;
						RiskIntPortsStore.actions.openPort(portId);
					}
				};

				const onLayerEnter = (e) => {
					map.getCanvas().style.cursor = "pointer";
					if (e.features.length > 0) {
						const coords = [e.lngLat.lng, e.lngLat.lat];
						let html = "";
						html += `<strong>${e.features[0].properties.name}</strong><br/>Click to view!`;
						PopupStore.actions.customHtml(html, 400, coords, true);
					}
				};
				const onLayerLeave = (e) => {
					map.getCanvas().style.cursor = MapStore.helpers.getDefaultCursor();
					PopupStore.actions.custom({}, null, false);
				};

				map.on("click", idName, onLayerClick);
				map.on("mouseenter", idName, onLayerEnter);
				map.on("mousemove", idName, onLayerEnter);
				map.on("mouseleave", idName, onLayerLeave);

				RiskIntPortsStore.meta.mapInitialised.set(true);
			}
		},

		deinitMap: () => {
			MapLayerManager.removeLayerGroup(MapLayerGroup.RISKINT_PORTS);
			RiskIntPortsStore.meta.mapInitialised.set(false);
		}

	}
};
export default RiskIntPortsStore;
